// src/pages/CRM/Core/AccountsList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  AccountType,
  AccountTypeLabels,
  AccountTypeColors,
  AccountStatus,
  AccountStatusLabels,
  Industries,
} from './crmEnums';

const AccountsList = () => {
  const navigate = useNavigate();
  const { chatbotId } = useParams();
  const location = useLocation();
  const fromFinance = location.state?.fromFinancePortal;
  const financeRedirectReason = location.state?.reason;
  const intendedFinancePath = location.state?.intendedPath;

  // Permission checks
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // State
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    account_type: '',
    industry: '',
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    account_type: AccountType.PROSPECT,
    industry: '',
    website: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPermission('crm', 'view');
    const add = await hasPermission('crm', 'add');
    const edit = await hasPermission('crm', 'edit');
    const del = await hasPermission('crm', 'delete');
    setCanView(view);
    setCanAdd(add);
    setCanEdit(edit);
    setCanDelete(del);
  };

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (chatbotId) params.append('chatbot_id', chatbotId);
      if (searchQuery) params.append('search', searchQuery);
      if (filters.status) params.append('status', filters.status);
      if (filters.account_type) params.append('account_type', filters.account_type);
      if (filters.industry) params.append('industry', filters.industry);

      const response = await axiosInstance.get(`/api/crm/accounts?${params.toString()}`);

      if (response.data.success) {
        setAccounts(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      } else {
        setError(response.data.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, [canView, page, rowsPerPage, sortBy, sortOrder, searchQuery, filters, chatbotId]);

  useEffect(() => {
    if (canView) {
      fetchAccounts();
    }
  }, [fetchAccounts, canView]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ status: '', account_type: '', industry: '' });
    setSearchQuery('');
    setPage(0);
  };

  const handleMenuOpen = (event, account) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewAccount = () => {
    if (selectedAccount) {
      navigate(`/chatbot/${chatbotId}/crm/clients/${selectedAccount.id}`);
    }
    handleMenuClose();
  };

  const handleEditAccount = () => {
    if (selectedAccount) {
      navigate(`/chatbot/${chatbotId}/crm/clients/${selectedAccount.id}?edit=true`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAccount) return;

    try {
      const response = await axiosInstance.delete(`/api/crm/accounts/${selectedAccount.id}`);

      if (response.data.success) {
        fetchAccounts();
        setDeleteDialogOpen(false);
        setSelectedAccount(null);
      } else {
        setError(response.data.message || 'Failed to delete account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  // Create account handlers
  const handleCreateDialogOpen = () => {
    setFormData({
      name: '',
      account_type: AccountType.PROSPECT,
      industry: '',
      website: '',
      phone: '',
    });
    setFormErrors({});
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setFormData({
      name: '',
      account_type: AccountType.PROSPECT,
      industry: '',
      website: '',
      phone: '',
    });
    setFormErrors({});
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Account name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const params = chatbotId ? `?chatbot_id=${chatbotId}` : '';
      const response = await axiosInstance.post(`/api/crm/accounts${params}`, formData);

      if (response.data.success) {
        handleCreateDialogClose();
        fetchAccounts();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to create account' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to create account',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatRevenue = (revenue) => {
    if (!revenue) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(revenue);
  };

  // -- FIXED URL HELPERS --
  const ensureAbsoluteUrl = (url) => {
    if (!url) return '';
    // Check if url starts with http:// or https://
    if (url.match(/^https?:\/\//i)) {
      return url;
    }
    // If not, prepend https://
    return `https://${url}`;
  };

  const getSafeHostname = (url) => {
    try {
      const safeUrl = ensureAbsoluteUrl(url);
      return new URL(safeUrl).hostname;
    } catch (e) {
      return url; // Fallback to original string if URL parsing fails
    }
  };
  // -----------------------

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">You do not have permission to view accounts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {fromFinance && (
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: '12px' }}
            action={
              intendedFinancePath ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(intendedFinancePath, { replace: true })}
                >
                  Try Again
                </Button>
              ) : null
            }
          >
            {financeRedirectReason || 'Add a client first to start using the Finance Portal.'}
          </Alert>
        )}

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Typography color="text.primary">Clients</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Clients
            </Typography>
            <Chip label={totalCount} color="primary" size="small" />
          </Box>
          {canAdd && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreateDialogOpen}
            >
              Add Client
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.account_type}
                label="Type"
                onChange={handleFilterChange('account_type')}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.values(AccountType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {AccountTypeLabels[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={handleFilterChange('status')}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(AccountStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {AccountStatusLabels[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Industry</InputLabel>
              <Select
                value={filters.industry}
                label="Industry"
                onChange={handleFilterChange('industry')}
              >
                <MenuItem value="">All Industries</MenuItem>
                {Industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Clear Filters">
                <IconButton onClick={clearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchAccounts}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Accounts Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Contacts</TableCell>
                <TableCell>Website</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No accounts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow
                    key={account.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients/${account.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          {getInitials(account.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {account.name}
                          </Typography>
                          {account.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {account.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={AccountTypeLabels[account.account_type]}
                        size="small"
                        color={AccountTypeColors[account.account_type]}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{account.industry || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" color="action" />
                        <Typography variant="body2">{account.contact_count || 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {account.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WebsiteIcon fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            component="a"
                            // Fixed: safely handle URL string
                            href={ensureAbsoluteUrl(account.website)}
                            target="_blank"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textDecoration: 'none', color: 'primary.main' }}
                          >
                            {/* Fixed: safely get hostname */}
                            {getSafeHostname(account.website)}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, account);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewAccount}>
          <BusinessIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={handleEditAccount}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Create Account Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Account</DialogTitle>
        <DialogContent>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.submit}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={formData.account_type}
                  label="Account Type"
                  onChange={handleFormChange('account_type')}
                >
                  {Object.values(AccountType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {AccountTypeLabels[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={formData.industry}
                  label="Industry"
                  onChange={handleFormChange('industry')}
                >
                  <MenuItem value="">Select Industry</MenuItem>
                  {Industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleFormChange('website')}
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleFormChange('phone')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedAccount?.name}</strong>?
            This action will archive the account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsList;